<template>
  <div
    id="kyc-container"
    class="kyc-container"
  >
    <vue-element-loading
      :active="loadingText !== ''"
      :text="loadingText"
      :text-style="{fontSize: '18px', color: '#2193ff', fontWeight: 'bold' }"
      is-full-screen
      spinner="bar-fade-scale"
      color="#2193ff"
      background-color="rgba(255, 255, 255, 0.5)"
      size="100"
    />
    <div class="kyc-container--item">
      <Header/>
    </div>
    <div class="section-gray">
      <div
        v-if="Object.keys(user).length && Object.keys(kycStatus).length"
        id="kyc-container--content"
        class="kyc-container--content"
      >
        <KYCStatusForDisplay
          v-if="user.kycStatus !== 'REQUEST_CHANGE'"
          :status="user.kycStatus"
          :name="kycStatus.status"
          :title="kycStatus.title"
          :subtitle="kycStatus.sub"
          :details="detailOfKycVerification"
          :is-loading="isLoadingKycVerificationData"
        />
        <KYCStatusForFix
          v-else-if="Object.keys(form).length"
          :nationality="form.nationality"
          :type-of-card="form.typeOfCard"
          :customer-type="form.customerType"
          :working-at-company-country="Object.keys(form).length ? form.workingAtCompanyCountry || '' : ''"
          :origin-country="Object.keys(form).length ? form.originCountry || '' : ''"
          :current-country="Object.keys(form).length ? form.currentCountry || '' : ''"
          :org-country="Object.keys(form).length ? form.orgCountry || '' : ''"
          :details="detailOfKycVerification"
          :countries="countries"
          :provinces-in-thailand="provincesInThailand"
          :occupation-list="occupationList"
          :business-list="businessList"
          :average-income-list="averageIncomeList"
          :kyc-topics-list="kycTopicsList"
          :is-loading-kyc-verification-data="isLoadingKycVerificationData"
          :is-loading-submit-kyc="isLoadingSubmitKyc"
          @submitFormAgain="submitKyc"
        />
        <div class="kyc-title">
          {{ $t('kyc.summary') }}
        </div>
        <div class="section-white">
          <SummaryPersonal
            v-if="form.customerType === 'Personal'"
            :user="user"
            :form="form"
            :provinces-in-thailand="provincesInThailand"
            :occupation-list="occupationList"
            :average-income-list="averageIncomeList"
            :is-loading="isLoadingFormData"
          />
          <SummaryOrganization
            v-else
            :user="user"
            :form="form"
            :provinces-in-thailand="provincesInThailand"
            :occupation-list="occupationList"
            :business-list="businessList"
            :average-income-list="averageIncomeList"
            :is-loading="isLoadingFormData"
          />
        </div>
      </div>
    </div>
    <div class="kyc-container--item">
      <Footer/>
    </div>
  </div>
</template>

<script>
import Header from '@/features/Header'
import Footer from '@/components/Footer'
import Service from './service.js'
import { kulapApi, awsS3Api } from '@/resources'
import { mapState } from 'vuex'
import cookie from '@/utils/cookie'
import SummaryPersonal from './components/SummaryPersonal'
import SummaryOrganization from './components/SummaryOrganization'
import KYCStatusForDisplay from './components/KYCStatusForDisplay'
import KYCStatusForFix from './components/KYCStatusForFix'
import { showToastError, base64toBlob, formatDate } from '@/utils/helper'

export default {
  name: 'SummaryOfKyc',
  components: {
    Header,
    Footer,
    SummaryPersonal,
    SummaryOrganization,
    KYCStatusForDisplay,
    KYCStatusForFix
  },
  provide () {
    return {
      $validator: this.$validator
    }
  },
  data () {
    return {
      service: null,
      loadingText: '',
      isLoadingFormData: false,
      isLoadingKycVerificationData: false,
      isLoadingSubmitKyc: false,
      form: {},
      detailOfKycVerification: [],
      countries: [],
      provincesInThailand: [],
      occupationList: [],
      businessList: [],
      averageIncomeList: [],
      kycTopicsList: []
    }
  },
  computed: {
    ...mapState({
      user: state => state.user
    }),
    kycStatus () {
      if (this.user && this.user.kycStatus) {
        if (this.user.kycStatus === 'VERIFYING') {
          return {
            status: this.$t('kyc.verifying'),
            title: this.$t('kyc.verifying_title'),
            sub: this.$t('kyc.verifying_detail')
          }
        } else if (this.user.kycStatus === 'REQUEST_CHANGE') {
          return {
            status: this.$t('kyc.request_change'),
            title: this.$t('kyc.request_change_title'),
            sub: this.$t('kyc.request_change_detail')
          }
        } else if (this.user.kycStatus === 'APPROVED') {
          return {
            status: this.$t('kyc.approved'),
            title: this.$t('kyc.approved_title'),
            sub: this.$t('kyc.approved_detail')
          }
        } else if (this.user.kycStatus === 'REJECTED') {
          return {
            status: this.$t('kyc.rejected'),
            title: this.$t('kyc.rejected_title'),
            sub: this.$t('kyc.rejected_detail')
          }
        }
        return {}
      }
      return {}
    }
  },
  async created () {
    if (!cookie.get('user') || !cookie.get('accessToken')) {
      this.$router.push({ name: 'Login', query: { next: 'SummaryOfKYC' } })
      return
    }
    this.service = new Service({
      store: this.$store,
      kulapApi,
      awsS3Api
    })
    this.getKycFormData()
    this.getKycVerificationData()
    const kulapUtilityFile = await this.service.getKulapUtilityFile()
    this.countries = kulapUtilityFile.countries
    this.provincesInThailand = kulapUtilityFile.provincesInThailand
    this.occupationList = kulapUtilityFile.occupationList
    this.businessList = kulapUtilityFile.businessList
    this.averageIncomeList = kulapUtilityFile.averageIncomeList
    this.kycTopicsList = kulapUtilityFile.kycTopicsList
    this.$scrollTo('#kyc-container--content')
  },
  mounted () {
    window.onpopstate = (event) => {
      if (this.$router.currentRoute.name === 'KYC') {
        this.$router.go(-2)
      }
    }
  },
  methods: {
    async getKycFormData () {
      this.isLoadingFormData = true
      await this.service.getUserProfile()
      this.$store.dispatch('setUser', this.user)
      this.form = await this.service.getKycFormData()
      this.isLoadingFormData = false
    },
    async getKycVerificationData () {
      this.isLoadingKycVerificationData = true
      try {
        const { logData } = await this.service.getKycVerification()
        this.detailOfKycVerification = logData
      } catch (e) {
        showToastError(e)
      }
      this.isLoadingKycVerificationData = false
    },
    async uploadDocsBeforeSubmit (form) {
      const listOfDocs = {
        identificationPicture: this.$t('kyc.photo_id_card').toLowerCase(),
        selfIdentificationPicture: this.$t('kyc.photo_selfie').toLowerCase(),
        governmentIssued: this.$t('kyc.gov_issued').toLowerCase(),
        proofOfIncome: this.$t('kyc.proof_of_income').toLowerCase(),
        assetOwnership: this.$t('kyc.evidence_assets').toLowerCase(),
        orgCompanyCert: this.$t('kyc.comp_cert_doc').toLowerCase(),
        orgShareholders: this.$t('kyc.list_shareholders_doc').toLowerCase(),
        orgSealCert: this.$t('kyc.seal_cert_doc').toLowerCase(),
        orgHouseRegisAuthDirector: this.$t('kyc.copy_house_registration').toLowerCase(),
        orgPowerAttorney: this.$t('kyc.power_attorney_lowercase')
      }
      const intersecKeys = Object.keys(listOfDocs).filter(key => Object.keys(form).includes(key))
      const promiseTasks = []
      intersecKeys.forEach(value => {
        const task = new Promise(async (resolve) => {
          if (/data:image/.test(form[value])) { // value is base64
            const blobImage = await base64toBlob(form[value])
            form[value] = await this.service.postUploadKycDocs(blobImage, value)
          } else if (/application\/pdf|image\/*/.test(form[value].type)) { // value is image or pdf file
            form[value] = await this.service.postUploadKycDocs(form[value], value)
          }
          resolve(true)
        })
        promiseTasks.push(task)
      })
      this.loadingText = 'Uploading documents...'
      await Promise.all(promiseTasks)
      delete form.inputFileSelfie
      delete form.inputFileIdCard
      return form
    },
    async submitKyc (form) {
      this.isLoadingSubmitKyc = true
      try {
        const validateFieldResult = await this.$validator.validate()
        if (!validateFieldResult) {
          const convertNameToId = Object.keys(this.errors.collect())[0].toLowerCase().replace(/ /g, '-').replace(/[()?.]/g, '')
          this.$scrollTo(`#${convertNameToId}`)
          this.isLoadingSubmitKyc = false
          return
        }
        const parseFormData = this.validateFormBeforeSubmit(form)
        const newForm = await this.uploadDocsBeforeSubmit(parseFormData)
        this.loadingText = `${this.$t('messages.submitting_your_info')}...`
        const result = await this.service.submitKyc(newForm)
        this.loadingText = ''
        await this.getKycFormData()
        await this.getKycVerificationData()
        if (result.success) {
          this.$buefy.toast.open({
            message: `Submit verification success.`,
            type: 'is-success'
          })
        } else {
          this.$buefy.toast.open({
            message: `Sorry, something went wrong.`,
            type: 'is-danger'
          })
        }
        this.$scrollTo('#kyc-container')
      } catch (e) {
        showToastError(e)
        this.loadingText = ''
      }
      this.isLoadingSubmitKyc = false
    },
    validateFormBeforeSubmit (form) {
      if (form.isLifetime) {
        form.expireDateOfId = 'Lifetime ID Card'
      } else if (form.hasOwnProperty('expireDateOfId')) {
        form.expireDateOfId = formatDate(form.expireDateOfId, 'YYYY-MM-DD').toString()
      }
      delete form.isLifetime
      if (form.hasOwnProperty('issueDateOfId')) {
        form.issueDateOfId = formatDate(form.issueDateOfId, 'YYYY-MM-DD').toString()
      }
      if (form.hasOwnProperty('birthdate')) {
        form.birthdate = formatDate(form.birthdate, 'YYYY-MM-DD').toString()
      }
      return form
    }
  }
}
</script>

<style lang="scss" scoped>
.kyc-container {
  float: left;
  width: 100%;
  min-height: 100vh;
  &--item {
    float: left;
    width: 100%;
  }
  &--content {
    max-width: 1024px;
    margin: 0 auto;
  }
}
.steps {
  display: block;
}
.number-steps {
  display: none;
}
.section-gray {
  float: left;
  width: 100%;
  padding-bottom: 80px;
}
.kyc-title {
  margin: 35px 0;
  font-size: 30px;
  font-weight: bold;
  text-align: center;
  color: #000000;
}
.kyc-status {
  font-size: 19px;
  font-weight: bold;
  color: #000000;
  width: 100%;
  margin-bottom: 15px;
  padding: 20px;

  span {
    border-radius: 21px;
    border: solid 2px;
    font-size: 13px;
    font-weight: bold;
    padding: 10px 10px;
    margin-left: 15px;
  }
}
.section-white {
  display: block;
  margin: 0 auto;
  padding: 50px;
  max-width: 1024px;
  width: calc(100% - 20px);
  border-radius: 3px;
  box-shadow: 0 0 6px -2px rgba(0, 0, 0, 0.5);
  background-color: #ffffff;
}
.is-button {
  font-size: 17px;
  font-weight: bold;
  color: #ffffff;
  height: auto;
  box-shadow: 0 2px 4px -2px rgba(0, 0, 0, 0.5);
  border: 0;
  text-align: center;
  border-radius: 3px;
  cursor: pointer;
}
.next-btn {
  background-color: #0559af;
  width: calc(50% - 20px);
  padding: 13px;
  margin: 24px 10px 0 0;
  float: right;
  max-width: 170px;
}
.full-next-btn {
  background-color: #0559af;
  width: calc(100% - 20px);
  padding: 13px;
  margin: 24px auto;
}

.submit-btn {
  background-color: #50e3c2;
  width: calc(50% - 20px);
  padding: 13px;
  margin: 24px 10px 0 0;
  float: right;
  max-width: 170px;
}
.back-btn {
  background-color: #616161;
  width: calc(50% - 20px);
  padding: 13px;
  margin: 24px 0 0 10px;
  float: left;
  max-width: 170px;
}
/* Less than tablet */
@media only screen and (max-width: 1087px) {
  .kyc-container {
    &--content {
      max-width: 768px;
    }
  }
  .section-white {
    max-width: 768px;
    width: calc(100% - 20px);
    padding: 20px;
  }
}
/* Mobile */
@media only screen and (max-width: 768px) {
  .kyc-container {
    &--content {
      max-width: 768px;
    }
  }
  .section-white {
    width: calc(100% - 20px);
    padding: 30px 15px;
  }
}
/* Custom for steps */
@media only screen and (max-width: 600px) {
  .steps {
    display: none;
  }
  .number-steps {
    display: block;
    text-align: center;
    font-size: 40px;
    font-weight: bold;
    color: #0559af;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/SummaryOfKYC/index.vue